import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const SessionLessonManager = ({ session, onBack }) => {
  const [assignedLessons, setAssignedLessons] = useState([]);
  const [availableLessons, setAvailableLessons] = useState([]);
  const [allLessons, setAllLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch session lessons
      const sessionResponse = await api.get(`/study-sessions/${session.session_id}/lessons`);
      setAssignedLessons(sessionResponse.data.lessons);
      
      // Fetch all lessons
      const allLessonsResponse = await api.get('/admin/lessons', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAllLessons(allLessonsResponse.data.lessons);
      
      // Fetch available lessons (not assigned to any session)
      const availableResponse = await api.get('/admin/study-sessions/available-lessons/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAvailableLessons(availableResponse.data.lessons);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch lessons",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignLesson = async (lesson_id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const newLessonIds = [...session.lesson_ids, lesson_id];
      
      await api.post(`/admin/study-sessions/${session.session_id}/assign-lessons`, 
        { lesson_ids: newLessonIds },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast({
        title: "Success",
        description: "Lesson assigned successfully"
      });
      
      // Update local state
      session.lesson_ids = newLessonIds;
      
      // Add delay to allow database updates to propagate
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to assign lesson",
        variant: "destructive"
      });
    }
  };

  const handleRemoveLesson = async (lesson_id) => {
    if (!window.confirm('Remove this lesson from the session?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const newLessonIds = session.lesson_ids.filter(id => id !== lesson_id);
      
      await api.post(`/admin/study-sessions/${session.session_id}/assign-lessons`, 
        { lesson_ids: newLessonIds },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast({
        title: "Success",
        description: "Lesson removed successfully"
      });
      
      // Update local state
      session.lesson_ids = newLessonIds;
      
      // Add delay to allow database updates to propagate
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to remove lesson",
        variant: "destructive"
      });
    }
  };

  const handleMoveLesson = async (lesson_id, direction) => {
    const currentIndex = session.lesson_ids.indexOf(lesson_id);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= session.lesson_ids.length) return;
    
    const newLessonIds = [...session.lesson_ids];
    [newLessonIds[currentIndex], newLessonIds[newIndex]] = [newLessonIds[newIndex], newLessonIds[currentIndex]];
    
    try {
      const token = localStorage.getItem('adminToken');
      
      await api.put(`/admin/study-sessions/${session.session_id}/reorder-lessons`, 
        { lesson_ids: newLessonIds },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast({
        title: "Success",
        description: "Lesson order updated"
      });
      
      // Update local state
      session.lesson_ids = newLessonIds;
      
      // Add delay to allow database updates to propagate
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reorder lessons",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6 flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Lessons</h1>
            <p className="text-gray-600 mt-1">{session.title}</p>
          </div>
          <Button onClick={onBack} variant="outline">
            Back
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Assigned Lessons */}
          <Card>
            <CardHeader>
              <CardTitle>Assigned Lessons ({assignedLessons.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {assignedLessons.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No lessons assigned yet</p>
              ) : (
                <div className="space-y-2">
                  {assignedLessons.map((lesson, index) => (
                    <div
                      key={lesson.lesson_id}
                      className="p-3 border rounded-lg bg-white"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{lesson.title}</p>
                          {lesson.reference && (
                            <p className="text-sm text-gray-600">{lesson.reference}</p>
                          )}
                          {lesson.description && (
                            <p className="text-sm text-gray-500 italic">{lesson.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">ID: {lesson.lesson_id} | Day {lesson.day_number}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            onClick={() => handleMoveLesson(lesson.lesson_id, 'up')}
                            disabled={index === 0}
                            variant="outline"
                            size="sm"
                          >
                            ↑
                          </Button>
                          <Button
                            onClick={() => handleMoveLesson(lesson.lesson_id, 'down')}
                            disabled={index === assignedLessons.length - 1}
                            variant="outline"
                            size="sm"
                          >
                            ↓
                          </Button>
                          <Button
                            onClick={() => handleRemoveLesson(lesson.lesson_id)}
                            variant="destructive"
                            size="sm"
                          >
                            ×
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Available Lessons */}
          <Card>
            <CardHeader>
              <CardTitle>Available Lessons ({availableLessons.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {availableLessons.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No available lessons</p>
              ) : (
                <div className="space-y-2">
                  {availableLessons.map((lesson) => (
                    <div
                      key={lesson.lesson_id}
                      className="p-3 border rounded-lg bg-white hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{lesson.title}</p>
                          {lesson.reference && (
                            <p className="text-sm text-gray-600">{lesson.reference}</p>
                          )}
                          {lesson.description && (
                            <p className="text-sm text-gray-500 italic">{lesson.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">ID: {lesson.lesson_id} | Day {lesson.day_number}</p>
                        </div>
                        <Button
                          onClick={() => handleAssignLesson(lesson.lesson_id)}
                          size="sm"
                        >
                          Add +
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SessionLessonManager;
